/**
 * Self-contained date formatting for the v2 sandbox/features.
 *
 * Deliberately NOT imported from `@app/utils/date` — the sandbox must run
 * with zero dependency on legacy modules so it stays portable and can be
 * deleted/relocated in one move once the migration is complete. When wiring
 * real data later, either keep this copy or point both at one shared module;
 * that decision is a one-line import change, not a rewrite.
 */

import { DateFormatTemplate, TimeFormat } from "@app/domain";
import { Languages } from "@app/types/multilingual";
import { t } from "@app/utils/translation";

const pad = (n: number) => n.toString().padStart(2, "0");

const monthName = (month: number) =>
  new Intl.DateTimeFormat("en-US", { month: "long" }).format(new Date(2000, month, 1));

/** Local-calendar-day key — only used to test whether two timestamps fall
 * on the same day (or N days apart) in the viewer's timezone. */
function dayKey(epochMs: number): string {
  const date = new Date(epochMs);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function startOfDay(epochMs: number): number {
  const date = new Date(epochMs);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/** Whole calendar days between `epochMs` and `referenceMs` (negative when
 * `epochMs` is in the past), independent of time-of-day. */
function calendarDayDiff(epochMs: number, referenceMs: number): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(epochMs) - startOfDay(referenceMs)) / msPerDay);
}

/** Time only, honoring the tenant's configured 12h/24h preference — see
 * `DateFormatTemplate`/`TimeFormat` in `domain/settings.types.ts`. */
export function formatEventTime(epochMs: number, timeFormat: TimeFormat): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: timeFormat === "12h",
  }).format(new Date(epochMs));
}

/** Date only, following the tenant's configured `dateFormat` template
 * (e.g. "dd MMMM yyyy") rather than a fixed locale-driven shape — the
 * template itself is the source of truth, not the browser's locale. */
export function formatDateByTemplate(epochMs: number, template: DateFormatTemplate): string {
  const date = new Date(epochMs);

  return template
    .replace("MMMM", monthName(date.getMonth())) // longer pattern first
    .replace("yyyy", date.getFullYear().toString())
    .replace("dd", pad(date.getDate()))
    .replace("MM", pad(date.getMonth() + 1));
}

/**
 * Date-group section header: localized "Today"/"Yesterday" (via
 * `Intl.RelativeTimeFormat`, so it follows the viewer's browser locale
 * exactly like the rest of this file's `Intl`-based formatting) for the two
 * most recent days, otherwise the tenant's configured `dateFormat`.
 */
export function formatDateHeader(epochMs: number, dateFormat: DateFormatTemplate): string {
  const dayDiff = calendarDayDiff(epochMs, Date.now());

  if (dayDiff === 0 || dayDiff === -1) {
    const label = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(dayDiff, "day");
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  return formatDateByTemplate(epochMs, dateFormat);
}

/**
 * Splits a chronologically-ordered event list into day groups, starting a
 * new group whenever consecutive events fall on different calendar days.
 */
export function groupEventsByDay<T extends { occurredAt: number }>(
  events: T[]
): { dayKey: string; occurredAt: number; events: T[] }[] {
  const groups: { dayKey: string; occurredAt: number; events: T[] }[] = [];

  events.forEach((event) => {
    const key = dayKey(event.occurredAt);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.dayKey === key) {
      lastGroup.events.push(event);
    } else {
      groups.push({ dayKey: key, occurredAt: event.occurredAt, events: [event] });
    }
  });

  return groups;
}

export function formatClockTime(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(epochMs));
}

export function formatShortDate(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
  }).format(new Date(epochMs));
}

export function formatDateTime(epochMs: number, language: Languages): string {
  const date = new Date(epochMs);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  return isToday
    ? `${t("Today", language)}, ${formatClockTime(epochMs)}`
    : `${formatShortDate(epochMs)}, ${formatClockTime(epochMs)}`;
}

export function formatRelativeToNow(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;

  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

export function formatEtaMinutes(minutes: number): string {
  if (minutes < 1) return "Arriving now";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest > 0 ? `${hours} hr ${rest} min` : `${hours} hr`;
}
